// valet-keychain-qr — qr-batch-generation: issue-a-new-batch form. Reuses the
// SAME Zod schema the backend/api-client validate against
// (CreateQrBatchRequestSchema from @jq4race/shared), so client-side bounds
// (quantity 1-500, prefix [A-Z0-9]{2,8}) can never drift from the server.

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@jq4race/i18n';
import { type CreateQrBatchRequest, CreateQrBatchRequestSchema } from '@jq4race/shared';
import { Button, Input, Label } from '@jq4race/ui';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export interface QrBatchFormProps {
  submitting: boolean;
  onSubmit: (input: CreateQrBatchRequest) => void;
}

// The shared schema's `prefix` is `.optional()` but still regex-validates a
// present value — an empty string from a blank <Input> would fail that regex
// even though "no override" is a valid, common choice. Preprocess blank →
// undefined so the form's "leave empty to reuse the stored prefix" UX matches
// the schema's actual optionality without touching the shared contract.
const QrBatchFormSchema = CreateQrBatchRequestSchema.extend({
  prefix: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
    CreateQrBatchRequestSchema.shape.prefix,
  ),
});

export function QrBatchForm({ submitting, onSubmit }: QrBatchFormProps) {
  const { t } = useTranslation('qr-generator');
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateQrBatchRequest>({
    resolver: zodResolver(QrBatchFormSchema),
    defaultValues: { quantity: 100, prefix: undefined },
  });

  return (
    <form
      onSubmit={handleSubmit((values) => onSubmit(values))}
      className="space-y-4"
      // Native HTML5 min/max constraint validation on the number input would
      // otherwise silently block form submission before react-hook-form's
      // zod resolver ever runs (min/max below are visual/a11y hints only —
      // the real bounds are enforced by CreateQrBatchRequestSchema).
      noValidate
    >
      <h3 className="text-[14px] font-medium text-jq-charcoal">{t('create.heading')}</h3>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="qr-quantity" className="mb-1.5 block text-[13px] font-medium">
            {t('create.quantityLabel')}
          </Label>
          <Input
            id="qr-quantity"
            type="number"
            min={1}
            max={500}
            disabled={submitting}
            aria-invalid={!!errors.quantity}
            {...register('quantity', { valueAsNumber: true })}
          />
          <p className="mt-1 text-[11px] text-jq-graphite">{t('create.quantityHelp')}</p>
          {errors.quantity && (
            <p role="alert" className="mt-1 text-[12px] text-jq-danger">
              {t('create.quantityRequired')}
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="qr-prefix" className="mb-1.5 block text-[13px] font-medium">
            {t('create.prefixLabel')}
          </Label>
          <Input
            id="qr-prefix"
            disabled={submitting}
            placeholder={t('create.prefixPlaceholder')}
            aria-invalid={!!errors.prefix}
            autoComplete="off"
            {...register('prefix')}
          />
          <p className="mt-1 text-[11px] text-jq-graphite">{t('create.prefixHelp')}</p>
          {errors.prefix && (
            <p role="alert" className="mt-1 text-[12px] text-jq-danger">
              {t('create.prefixInvalid')}
            </p>
          )}
        </div>
      </div>
      <Button type="submit" size="sm" disabled={submitting}>
        {submitting ? t('create.submitting') : t('create.submit')}
      </Button>
    </form>
  );
}
